import React from 'react';
import { Autocomplete, Box, CircularProgress, Stack, TextField } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { ControlledStaticDatePicker } from '../../../Components/CustomUIElements/SingleDatePicker';
import { Virtualize } from '../../../Components/CustomUIElements/VirtualizedAutocomplete';
import { sharedClasses } from '../../../Components/CustomUIElements/sharedClasses';
import { EmptyState } from '../../../Components/CustomUIElements/EmptyState';
import { NoEventsSVG } from '../../../Components/Icons';
import { InviteToTimeslotStep1Props } from '../../types';
import SelectedApplicants from '../SelectedApplicants';
import { classes } from '../styles';
import formatToUserTimeZone from '../../../utils/formatToUserTimeZone';

export default function InviteToTimeslotStep1({
  eventTypes,
  selectedEventType,
  setSelectedEventType,
  errors,
  setErrors,
  filterType,
  setFilterType,
  selectedRecruiter,
  setSelectedRecruiter,
  selectedJob,
  setSelectedJob,
  recruiters,
  loadingRecruiters,
  jobOptions,
  loadingJobs,
  allEvents,
  events,
  isLoading,
  spots,
  globalSelected,
  handleDeselectCandidate,
  filters,
  selectedDay,
  setSelectedDay,
  setSelectedMonth,
  timeslotHovered,
  setTimeslotHovered,
  eventTypeRef,
  isApplicationPage
}: InviteToTimeslotStep1Props) {
  const filterOptions = [
    { label: 'Created by', value: 'owner' },
    { label: 'Job', value: 'job' },
    { label: 'Recruiter', value: 'recruiter' }
  ] as const;

  const eventTypeCounts = React.useMemo(() => {
    const counts: Record<number, number> = {};
    allEvents.forEach((event) => {
      counts[event.event_type.id] = (counts[event.event_type.id] || 0) + 1;
    });
    return counts;
  }, [allEvents]);

  const disableDate = (date: Date) => {
    const datelist = events.map((e) => formatToUserTimeZone(e.begins_at, 'ymd'));
    return !selectedEventType ? true : !datelist.includes(formatToUserTimeZone(date, 'ymd'));
  };

  const sameDay = (date: string) => {
    if (!selectedDay) return false;
    return formatToUserTimeZone(date, 'ymd') === formatToUserTimeZone(selectedDay, 'ymd');
  };

  const applicationCount = globalSelected ? globalSelected.length : 1;

  return (
    <Stack sx={{ rowGap: 2 }}>
      <Autocomplete
        options={eventTypes}
        getOptionLabel={(option) => `${option.name} (${option.duration} minutes)`}
        onChange={(_event, value) => {
          value && setErrors({ ...errors, eventType: '' });
          setSelectedEventType(value);
        }}
        value={selectedEventType}
        sx={{ ...sharedClasses.formAutocomplete, width: '387px', marginTop: 3 }}
        slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
        renderOption={(props, option) => (
          <li {...props}>
            {option.name} ({option.duration} minutes)
            <Box
              component="span"
              sx={{ fontStyle: 'italic', color: 'text.secondary', marginLeft: 1 }}
            >
              {eventTypeCounts[option.id] || 0} timeslot
              {eventTypeCounts[option.id] === 1 ? '' : 's'} available
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Event type"
            slotProps={{ inputLabel: { shrink: true } }}
            required
            sx={sharedClasses.autoCompleteTextfield}
            helperText={errors.eventType}
          />
        )}
        ref={eventTypeRef}
      />
      <Box sx={{ display: 'flex', flexDirection: 'row', columnGap: 2 }}>
        <Autocomplete
          options={filterOptions}
          getOptionLabel={(option) => option.label}
          value={filterOptions.find((o) => o.value === filterType) || filterOptions[0]}
          onChange={(_, v) => {
            setFilterType(v?.value ?? 'owner');
            setSelectedRecruiter(null);
            setSelectedJob(null);
          }}
          sx={{ ...sharedClasses.formAutocomplete, width: '387px', marginTop: 3 }}
          slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Filter by"
              slotProps={{ inputLabel: { shrink: true } }}
              sx={sharedClasses.autoCompleteTextfield}
              helperText={errors.recruiter}
            />
          )}
        />
        {(filterType === 'owner' || filterType === 'recruiter') && (
          <Autocomplete
            options={recruiters}
            loading={loadingRecruiters}
            getOptionLabel={(option) => option.name}
            onChange={(_event, value) => {
              value && setErrors({ ...errors, recruiter: '' });
              setSelectedRecruiter(value);
            }}
            value={selectedRecruiter}
            sx={{ ...sharedClasses.formAutocomplete, width: '387px', marginTop: 3 }}
            slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={filterType === 'owner' ? 'Created by' : 'Recruiter'}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={sharedClasses.autoCompleteTextfield}
                helperText={errors.recruiter}
              />
            )}
          />
        )}
        {filterType === 'job' && (
          <Virtualize
            value={selectedJob}
            passOptions={jobOptions || []}
            setValue={async (event: string, value: string) => {
              setSelectedJob(value);
            }}
            hasIdAndRef={true}
            passedStyles={{
              ...sharedClasses.formAutocomplete,
              width: '387px',
              marginTop: 3
            }}
            loading={loadingJobs}
            loadingText="Loading jobs..."
            label="Job"
            freeSolo={false}
          />
        )}
      </Box>
      <Box sx={classes.noticeContainer}>
        <InfoIcon sx={classes.infoIcon} />
        <Box sx={{ marginLeft: 1 }}>
          All upcoming timeslots shown below will be sent by the system.
        </Box>
      </Box>
      {selectedEventType && (
        <Box
          sx={{
            color: spots < applicationCount ? 'error.main' : 'inherit',
            fontSize: 16
          }}
        >
          <Box component="span" fontWeight="bold">
            {spots}
          </Box>{' '}
          booking spots in{' '}
          <Box component="span" fontWeight="bold">
            {events.length}
          </Box>{' '}
          upcoming time slots available
        </Box>
      )}
      <Box sx={classes.timeslotsContainer}>
        <Box sx={classes.calendar}>
          <ControlledStaticDatePicker
            displayStaticWrapperAs="desktop"
            views={['day']}
            value={selectedDay}
            shouldDisableDate={disableDate}
            onChange={(value) => setSelectedDay(value)}
            onMonthChange={(value) => {
              setSelectedMonth(formatToUserTimeZone(value, 'ymd'));
            }}
          />
        </Box>
        <Box sx={classes.dailyTimeslots}>
          <Box sx={classes.dailyTimeslotsHeader}>Timeslots</Box>
          <Box sx={classes.dailyTimeslotsContainer}>
            {events.length > 0 &&
              !isLoading.events &&
              events
                .filter((event) => sameDay(event.begins_at))
                .map((event) => (
                  <Box
                    key={event.id}
                    sx={classes.timeslot}
                    onMouseEnter={() => {
                      setTimeslotHovered(event.id);
                    }}
                    onMouseLeave={() => {
                      setTimeslotHovered(null);
                    }}
                  >
                    <Box sx={classes.timeslotTime}>
                      {formatToUserTimeZone(event.begins_at, 'onlyTime')}
                    </Box>
                    <Box sx={{ ...classes.timeslotType, ...classes.ellipsis }}>{event.subject}</Box>
                    {timeslotHovered === event.id && (
                      <Box sx={classes.expandedTimeslot}>
                        <Box sx={classes.timeslotTime}>
                          {formatToUserTimeZone(event.begins_at, 'onlyTime')}
                        </Box>
                        <Box sx={classes.timeslotType}>{event.subject}</Box>
                        {event.note && <Box sx={classes.timeslotNote}>{event.note}</Box>}
                      </Box>
                    )}
                  </Box>
                ))}
            {!isLoading.events &&
              events.filter((event) => sameDay(event.begins_at)).length === 0 && (
                <Box sx={classes.noTimeslots}>
                  <EmptyState svgIcon={<NoEventsSVG />} label="No timeslots on selected date" />
                </Box>
              )}
            {isLoading.events && (
              <Box sx={classes.noTimeslots}>
                <CircularProgress size={32} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      {!isApplicationPage && (
        <Box sx={{ marginLeft: '8px' }}>
          <Stack sx={classes.modalCandidatesHeader}>Candidates</Stack>
          <SelectedApplicants
            globalSelected={globalSelected}
            handleDeselectCandidate={handleDeselectCandidate}
            closeModalCallback={() => {
              // No-op since this is a step in a multi-step form
            }}
            filters={filters}
          />
        </Box>
      )}
    </Stack>
  );
}
